import React, { type ComponentProps, type RefObject, useCallback, useEffect, useRef } from 'react';
import { type LayoutChangeEvent, type View, ViewProps } from 'react-native';
import Animated from 'react-native-reanimated';

// ─────────────────────────────────────────────────────────────────────────────

export const ScrollspyV2Target = (props: ScrollspyV2TargetProps) => {
  const { targetId, measureCoordinates, deregisterTarget, children, onLayout, ...viewProps } =
    props;

  // ─── Refs ──────────────────────────────────────────────────────────────

  const ref = useRef<View>(null);

  // ─── Handlers ──────────────────────────────────────────────────────────

  const handleLayout = useCallback(
    (event: LayoutChangeEvent) => {
      measureCoordinates(targetId, ref);
      onLayout?.(event);
    },
    [measureCoordinates, targetId, onLayout],
  );

  const deregisterOnUnmount = useCallback(() => {
    return () => {
      deregisterTarget(targetId);
    };
  }, [targetId, deregisterTarget]);

  // ─── Effects ───────────────────────────────────────────────────────────

  useEffect(deregisterOnUnmount, [deregisterOnUnmount]);

  // ─────────────────────────────────────────────────────────────────────

  return (
    <Animated.View ref={ref} onLayout={handleLayout} {...viewProps}>
      {children}
    </Animated.View>
  );
};

// ─── Types ───────────────────────────────────────────────────────────────────

type ScrollspyV2TargetProps = {
  targetId: string;
  measureCoordinates: (targetId: string, viewRef: RefObject<View | null>) => void;
  deregisterTarget: (targetId: string) => void;
  onLayout?: ViewProps['onLayout'];
} & Omit<ComponentProps<typeof Animated.View>, 'ref'>;
